// ** React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// ** MUI Imports
import {
  Box,
  Button,
  Divider,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material'

// ** Componets Imports
import CustomDialog from '../../../../components/dialog'
import Icon from '../../../../components/icon'
import OverlayLoading from '../../../../components/overlay-loading'
import ReadOnlyComponent from '../../../../components/read-only-label'
import RequiredLabelComponent from '../../../../components/required-label-component'
import ResumeUploadModal from '../../../../components/resume-upload/ResumeUploadModal'
import TooltipText from '../../../../components/TooltipText'

// **API Imports
import { downloadRegistrationFile } from '../../../../axios/api/candidate'
import {
  exportAssessmentCandidateResume,
  fetchUserDetails,
  skipPrerequisiteCheck,
  uploadCandidateResume
} from '../../../../axios/api/contest'

// ** Utils Imports
import { CANDIDATE_STATUS } from '../../../../utils/constant'
import { TOOLTIPS } from '../../../../utils/tooltips'
import {
  copyToClipboard,
  downloadPDFBlob,
  formatDateTime,
  getPrerequisiteLabelAndColor,
  hexToRGBA,
  showErrorToast
} from '../../../../utils/utils'

// **Third party Imports
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'

// **SCSS Imports
import CandidateStageHistoryModal from './CandidateStageHistoryModal'
import './index.scss'

// styled component
const Label = styled(Typography)(() => ({
  fontSize: '0.8125rem',
  marginBottom: '0.15rem',
  lineHeight: '1.054',
  color: 'var(--text-secondary)'
}))

const FieldComponent = memo(({ fieldConfig, handleDownload }) => {
  // variables
  const displayValue = useMemo(() => {
    return fieldConfig?.type === 'multi_select_dropdown'
      ? fieldConfig?.value || []
      : fieldConfig?.type === 'date'
      ? fieldConfig?.value
        ? formatDateTime(fieldConfig?.value, true, false)
        : '-'
      : fieldConfig?.value
  }, [fieldConfig?.type, fieldConfig?.value])

  // handlers
  const shortenMiddle = useCallback((text, startChars = 10, endChars = 8) => {
    if (text?.length <= startChars + endChars + 3) return text
    return `${text?.slice(0, startChars)}...${text.slice(-endChars)}`
  }, [])

  return (
    <TableRow
      key={`field-row-${fieldConfig?.id}`}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row" sx={{ maxWidth: '180px' }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: '700', textWrap: 'wrap' }}
        >
          {fieldConfig?.label}
        </Typography>
      </TableCell>
      <TableCell align="left">
        {fieldConfig?.type !== 'file' &&
        fieldConfig?.type !== 'resume' &&
        fieldConfig?.type !== 'url' ? (
          <ReadOnlyComponent
            key={fieldConfig?.id}
            displayValue={displayValue}
            showInChips={fieldConfig?.type === 'multi_select_dropdown'}
            valueStyles={{ wordBreak: 'break-word', fontSize: '12px' }}
          />
        ) : fieldConfig?.type === 'url' ? (
          <>
            {fieldConfig?.value ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <a
                  href={
                    fieldConfig?.value.trim().startsWith('http://') ||
                    fieldConfig?.value.trim().startsWith('https://')
                      ? fieldConfig?.value.trim()
                      : `https://${fieldConfig?.value.trim()}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--primary-main)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    wordBreak: 'break-word'
                  }}
                >
                  {fieldConfig?.value.trim()}
                </a>

                {fieldConfig?.value && (
                  <Box
                    onClick={() => copyToClipboard(fieldConfig?.value.trim())}
                    sx={{
                      cursor: 'pointer',
                      color: 'var(--text-secondary)',
                      '&:hover': {
                        color: 'var(--primary-main)'
                      }
                    }}
                  >
                    <Icon
                      icon={'mdi:clipboard-multiple-outline'}
                      style={{ fontSize: '14px' }}
                      hoverEffect
                      title="Copy"
                    />{' '}
                  </Box>
                )}
              </Box>
            ) : (
              <Typography>-</Typography>
            )}
          </>
        ) : (
          <>
            {fieldConfig?.value ? (
              <Button
                size="small"
                variant="outlined"
                endIcon={
                  <Icon icon={'mdi:download'} style={{ fontSize: '14px' }} />
                }
                title={fieldConfig?.value}
                sx={{
                  maxWidth: '100%',
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-all',
                  fontSize: '12px'
                }}
                onClick={() => {
                  handleDownload(fieldConfig?.id, fieldConfig?.value)
                }}
              >
                {fieldConfig?.value
                  ? shortenMiddle(fieldConfig?.value?.trim(), 16, 12) || '-'
                  : '-'}
              </Button>
            ) : (
              <Typography>-</Typography>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  )
})

// components
const CandidateFormInfoLabel = memo(
  ({ label, value, suffix = '', needCopyToClipboard = false }) => {
    return (
      <Box>
        <Label variant="body1">{label}</Label>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <TooltipText className="ellipsis-text" text={value || '-'} />
          <TooltipText className="ellipsis-text" text={value ? suffix : ''} />
          {value && needCopyToClipboard ? (
            <Icon
              onClick={() => copyToClipboard(value)}
              hoverEffect
              icon="mdi:clipboard-multiple-outline"
              title={TOOLTIPS.copy}
              style={{ fontSize: '16px', color: 'var(--text-secondary)' }}
            />
          ) : (
            <></>
          )}
        </Box>
      </Box>
    )
  }
)

// components
const SystemCheckDetails = memo(
  ({ label, value, icon, color = '', suffix = '' }) => {
    return (
      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <Box>
          <Icon
            icon={icon || 'codicon:settings'}
            style={{ cursor: 'default', color: 'var(--primary-main)' }}
          />
        </Box>
        <Box>
          <Label variant="body1">{label}</Label>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: color
            }}
          >
            {value || '-'} {value ? suffix : ''}
          </Typography>
        </Box>
      </Box>
    )
  }
)

const PrerequisiteResultDialog = ({
  open = false,
  config = {},
  handleClose = () => {},
  handleSkipPrerequisiteCheck,
  getcolor,
  getValue,
  allowSkipPrerequisiteCheck,
  disableEnforceFocus
}) => {
  const { prerequisiteResult, canUploadResume } = config
  return (
    <CustomDialog
      open={open}
      disableEnforceFocus={disableEnforceFocus}
      title={'Candidate System Checks'}
      handleClose={handleClose}
      className="candidate-info-modal"
      dialogPaperStyle={{
        width: '70%',
        maxWidth: '1000px',
        '& .MuiDialogContent-root': {
          overflowY: 'hidden',
          display: 'flex',
          padding: '0px !important'
        }
      }}
    >
      <Box className="candidate-system-checks-container">
        <Grid container spacing={4} className="candidate-system-container">
          <Grid item xs={12} sx={{ fontSize: '14px', fontWeight: 600 }}>
            1. Candidate Eligibility for contest
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <SystemCheckDetails
              label={'Overall System Check'}
              value={getPrerequisiteLabelAndColor(config)?.label}
              icon={'bi:check-all'}
              color={getPrerequisiteLabelAndColor(config)?.labelColor}
            />

            {allowSkipPrerequisiteCheck &&
              !prerequisiteResult?.skipPrerequisite &&
              !prerequisiteResult?.prerequisiteApproved &&
              canUploadResume && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleSkipPrerequisiteCheck({
                      id: config?.id,
                      email: config?.email
                    })
                  }}
                >
                  Skip System Checks
                </Button>
              )}
          </Grid>
          <Grid item xs={12} sx={{ fontSize: '14px', fontWeight: 600 }}>
            2. Media and connectivity
          </Grid>
          <Grid item md={6} xs={12}>
            <SystemCheckDetails
              label={'Internet Speed Check'}
              value={getValue(prerequisiteResult?.internetSpeedApproved)}
              icon={'ic:sharp-speed'}
              color={getcolor(prerequisiteResult?.internetSpeedApproved)}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <SystemCheckDetails
              label={'Internet Speed '}
              value={prerequisiteResult?.internetSpeed}
              icon={'ic:sharp-speed'}
              suffix={' Mbps'}
            />
          </Grid>

          {prerequisiteResult?.mediaSharingApproved !== null && (
            <Grid item md={6} xs={12}>
              <SystemCheckDetails
                label={'Media Sharing Check'}
                value={getValue(prerequisiteResult?.mediaSharingApproved)}
                icon={'uil:webcam'}
                color={getcolor(prerequisiteResult?.mediaSharingApproved)}
              />
            </Grid>
          )}
          {prerequisiteResult?.micTestApproved !== null && (
            <Grid item md={6} xs={12}>
              <SystemCheckDetails
                label={'Microphone Check'}
                value={getValue(prerequisiteResult?.micTestApproved)}
                icon={'fluent:mic-record-24-filled'}
                color={getcolor(prerequisiteResult?.micTestApproved)}
              />
            </Grid>
          )}
          {prerequisiteResult?.screenSharingApproved !== null && (
            <Grid item md={6} xs={12}>
              <SystemCheckDetails
                label={'Screen Sharing Check'}
                value={getValue(prerequisiteResult?.screenSharingApproved)}
                icon={'material-symbols:screen-share-outline'}
                color={getcolor(prerequisiteResult?.screenSharingApproved)}
              />
            </Grid>
          )}

          <Grid item xs={12} sx={{ fontSize: '14px', fontWeight: 600 }}>
            3. Device Information
          </Grid>

          <Grid item md={6} xs={12}>
            <SystemCheckDetails
              label={'Operating System'}
              value={prerequisiteResult?.metadata?.os}
              icon={'codicon:server-process'}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <SystemCheckDetails
              label={'Browser '}
              value={prerequisiteResult?.metadata?.browser}
              icon={'mdi:web'}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <SystemCheckDetails
              label={'Platform '}
              value={prerequisiteResult?.metadata?.platform}
              icon={'streamline:computer-pc-desktop'}
            />
          </Grid>
        </Grid>
      </Box>
    </CustomDialog>
  )
}

const CandidateInfoModal = ({
  candidateId,
  open = false,
  refreshData,
  handleClose,
  isAssessment = false,
  contestId = null,
  canUploadResume = true,
  allowSkipPrerequisiteCheck = false,
  disableEnforceFocus = false
}) => {
  // hooks
  const selectedContest = useSelector((state) => state.contest.selectedContest)

  // states
  const [uploadModal, setUploadModal] = useState(false)
  const [showPrerequisiteResult, setShowPrerequisiteResult] = useState(false)
  const [candidateDetails, setCandidateDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [showStagesHistory, setShowStagesHistory] = useState(null)

  // variables
  const isSkipped = useMemo(() => {
    return candidateDetails?.prerequisiteResult?.skipPrerequisite || false
  }, [candidateDetails])

  const currentContestId = useMemo(() => {
    return selectedContest?.general?.id || contestId
  }, [selectedContest, contestId])

  // handlers
  const fetchCandidateDetails = useCallback(async () => {
    setLoading(true)
    const response = await fetchUserDetails(currentContestId, candidateId)
    setLoading(false)

    if (response?.isError) {
      showErrorToast({
        ...response,
        message: response?.message || 'Error fetching candidate details'
      })
    } else {
      setCandidateDetails(response)
    }
  }, [currentContestId, candidateId])

  const handleResumeUpload = useCallback(
    async (file) => {
      setUploadLoading(true)
      const formdata = new FormData()
      formdata.append('resume', file)
      const response = await uploadCandidateResume(
        currentContestId,
        candidateId,
        formdata
      )
      setUploadLoading(false)
      if (response?.isError) {
        showErrorToast(response)
      } else {
        setUploadModal(false)
        toast.success('Resume Uploaded Successfully')

        if (refreshData) {
          refreshData()
        }
        fetchCandidateDetails()
      }
    },
    [fetchCandidateDetails, refreshData, currentContestId, candidateId]
  )

  const handleDownloadResume = useCallback(async () => {
    setDownloadLoading(true)

    const response = await exportAssessmentCandidateResume(
      currentContestId,
      candidateDetails?.id
    )
    if (response?.isError) {
      showErrorToast(response)
    } else {
      downloadPDFBlob(response, candidateDetails?.fileName)
    }

    setDownloadLoading(false)
  }, [currentContestId, candidateDetails?.id, candidateDetails?.fileName])

  // Registration file download handler
  const handleDownloadFile = useCallback(
    async (email, fieldId, fileName) => {
      setDownloadLoading(true)
      const response = await downloadRegistrationFile(contestId, email, fieldId)
      setDownloadLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        downloadPDFBlob(response, fileName)
      }
    },
    [contestId]
  )

  const handleSkipPrerequisiteCheck = useCallback(
    (data) => {
      const payload = {
        contestId: currentContestId,
        candidateId: data?.id
      }
      Swal.fire({
        icon: 'info',
        text: `Are you sure you want to skip the system checks for ${data?.email}?`,
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        showCancelButton: true
      }).then(async (result) => {
        if (result.isConfirmed) {
          setLoading(true)
          const response = await skipPrerequisiteCheck(payload)
          setLoading(false)
          if (response?.isError) {
            showErrorToast({
              ...response,
              message: response?.message || 'Error skipping system checks'
            })
          } else {
            toast.success(response || 'System checks skipped for candidate')
            if (refreshData) {
              refreshData()
              fetchCandidateDetails()
            }
          }
        }
      })
    },
    [currentContestId, refreshData, fetchCandidateDetails]
  )

  useEffect(() => {
    if (open) {
      fetchCandidateDetails()
    }
  }, [open, fetchCandidateDetails])

  const CANDIDATE_DETAILS_FIELD = [
    { label: 'Full Name', value: 'name' },
    { label: 'Email', value: 'email' },
    { label: 'Phone Number', value: 'phoneNumber' },
    { label: 'Status', value: 'status', showchip: true }
  ]

  const getcolor = useCallback(
    (value) => {
      if (value === null) {
        if (isSkipped) {
          return 'var(--primary-main)'
        }
        return 'var(--warning-main)'
      } else if (value) {
        return 'var(--success-main)'
      } else {
        return 'var(--error-main)'
      }
    },
    [isSkipped]
  )

  const getValue = useCallback(
    (value, YesNoValue = false) => {
      if (YesNoValue) {
        return value ? 'Yes' : 'No'
      }

      return value === null
        ? isSkipped
          ? '-'
          : 'Pending'
        : value
        ? 'Passed'
        : 'Failed'
    },
    [isSkipped]
  )

  const dialogHeader = useCallback(() => {
    return (
      <Box sx={{ display: 'flex', gap: '12px' }}>
        <Button
          startIcon={
            <Icon fontSize={18} icon={'icon-park-twotone:history-query'} />
          }
          variant="contained"
          size="small"
          onClick={() => setShowStagesHistory(true)}
        >
          Stages History
        </Button>
        {candidateDetails?.prerequisiteResult && (
          <Button
            startIcon={<Icon fontSize={18} icon={'grommet-icons:system'} />}
            variant="contained"
            size="small"
            onClick={() => setShowPrerequisiteResult(true)}
          >
            System Checks
          </Button>
        )}
        {isAssessment && canUploadResume && (
          <Button
            startIcon={<Icon fontSize={18} icon={'material-symbols:upload'} />}
            variant="contained"
            size="small"
            onClick={() => setUploadModal(true)}
          >
            {candidateDetails?.hasResume ? 'Replace Resume' : 'Upload Resume'}
          </Button>
        )}
      </Box>
    )
  }, [
    candidateDetails?.hasResume,
    candidateDetails?.prerequisiteResult,
    isAssessment,
    canUploadResume
  ])

  return (
    <>
      <CustomDialog
        open={open}
        disableEnforceFocus={disableEnforceFocus}
        title={'Candidate Information'}
        handleClose={handleClose}
        outsideClickClose={false}
        className="candidate-info-modal"
        dialogPaperStyle={{
          width: '70%',
          maxWidth: '800px',
          '& .MuiDialogContent-root': {
            overflowY: 'hidden',
            display: 'flex',
            padding: '0px !important'
          }
        }}
        renderHeader={dialogHeader}
      >
        {(loading || downloadLoading) && <OverlayLoading />}
        {!loading && open && (
          <Box className="candidate-info-modal-inner-container">
            <Box className="candidate-general-details-container">
              <Grid
                container
                spacing={4}
                className="candidate-general-details-inner-container"
              >
                <Grid item md={'auto'} xs={12}>
                  <Box className="candidate-profile-container">
                    {candidateDetails?.profilePicUri ? (
                      <img
                        src={candidateDetails?.profilePicUri}
                        alt="profile"
                      />
                    ) : (
                      <Icon
                        icon="radix-icons:avatar"
                        width="120"
                        height="120"
                        style={{ cursor: 'default' }}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid
                  item
                  md={8}
                  xs={12}
                  container
                  spacing={4}
                  className="candidate-details-container"
                >
                  {CANDIDATE_DETAILS_FIELD?.map((item, index) => {
                    return (
                      <Grid item md={12} lg={6} key={item?.label || index}>
                        <CandidateFormInfoLabel
                          label={item?.label}
                          value={
                            item?.label === 'Status'
                              ? candidateDetails?.[item?.value] ===
                                CANDIDATE_STATUS.ACTIVE
                                ? 'Active'
                                : 'Pending'
                              : candidateDetails?.[item?.value]
                          }
                          needCopyToClipboard={item?.label !== 'Status'}
                        />
                      </Grid>
                    )
                  })}

                  {candidateDetails?.hasResume && (
                    <Grid item md={12} lg={6}>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <RequiredLabelComponent label="Resume" />
                        <Typography
                          sx={{
                            width: 'max-content',
                            cursor: 'pointer',
                            color: '#0000ee',
                            textDecoration: 'underline'
                          }}
                          onClick={handleDownloadResume}
                        >
                          {candidateDetails?.fileName}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* // Disqualified chip */}
                  {candidateDetails?.lateEntryDisqualified && (
                    <Grid item xs={12}>
                      <Box sx={{ width: 'max-content' }}>
                        <Button
                          color="error"
                          sx={{
                            backgroundColor: hexToRGBA('#EA5455', 0.1),
                            cursor: 'default',
                            pointerEvents: 'none'
                          }}
                          startIcon={
                            <Icon
                              icon="majesticons:restricted"
                              style={{
                                color: hexToRGBA('var(--error-main)', 0.2),
                                cursor: 'default'
                              }}
                            />
                          }
                        >
                          Disqualified due to late entry.
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Box>
            {candidateDetails?.formData && (
              <>
                <Divider />
                <Typography variant="h5" sx={{ fontWeight: '700' }}>
                  Candidate Application Details
                </Typography>
                <Box className="candidate-registration-form-details">
                  <TableContainer className="candidate-registration-form-details-table">
                    <Table size="small" aria-label="a dense table">
                      <TableBody>
                        {candidateDetails?.formData?.map((item, index) => {
                          return (
                            <FieldComponent
                              key={`${item?.id}-${index}`}
                              fieldConfig={item}
                              handleDownload={(fieldId, fileName) => {
                                handleDownloadFile(
                                  candidateDetails?.email,
                                  fieldId,
                                  fileName
                                )
                              }}
                            />
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </Box>
        )}
      </CustomDialog>
      <ResumeUploadModal
        open={uploadModal}
        loading={uploadLoading}
        handleSubmit={handleResumeUpload}
        handleClose={() => setUploadModal(false)}
      />
      <PrerequisiteResultDialog
        open={showPrerequisiteResult}
        config={{ ...candidateDetails, canUploadResume }}
        handleClose={() => setShowPrerequisiteResult(false)}
        handleSkipPrerequisiteCheck={handleSkipPrerequisiteCheck}
        getcolor={getcolor}
        getValue={getValue}
        disableEnforceFocus={disableEnforceFocus}
        allowSkipPrerequisiteCheck={allowSkipPrerequisiteCheck}
      />

      {showStagesHistory && (
        <CandidateStageHistoryModal
          open={true}
          handleClose={() => {
            setShowStagesHistory(null)
          }}
          contestId={currentContestId}
          candidateId={candidateId}
          assessment={isAssessment}
        />
      )}
    </>
  )
}

export default memo(CandidateInfoModal)
